// useNearestTrail.ts
// ==================
//
// Wave 14ca-fix64: identifiziert den naechstgelegenen bekannten Trail zur
// aktuellen GPS-Position. User-Wunsch waehrend Ride: "ich will den Namen
// des Trails sehen, sofern ich mich auf einem Trail befinde".
//
// Strategie:
//   1. Subscribe positionBus
//   2. Bei jedem Position-Update: nearestPointOnTrack fuer jeden Trail
//      aus dem cached Trail-Set
//   3. Den Trail mit der kleinsten off-track-Distanz behalten,
//      wenn off-track <= MAX_OFF_TRACK_M (default 30 m)
//   4. Returnt {trail, alongTrackMeters, offTrackMeters} oder null
//
// Performance:
//   - 28 Trails × nearestPointOnTrack ≈ ~30 ms pro GPS-Tick (beim
//     einmaligen Liguria-Set). Das passt locker in den 1-Hz-watchPosition-
//     Rhythmus.
//   - Kleine Optimierung: wir cachen die cumulative-distance-Arrays pro
//     Trail in einer WeakMap, sodass wir sie nicht bei jedem Tick neu
//     berechnen muessen.

import { useEffect, useState, useRef } from 'react';
import type { CachedTrail } from './trails';
import { nearestPointOnTrack, cumulativeDistances } from './trailGeom';
import { positionBus } from './positionBus';

const MAX_OFF_TRACK_M = 30;

export interface NearestTrailResult {
  trail: CachedTrail;
  /** Distanz entlang des Tracks vom Start, in Metern. */
  alongTrackMeters: number;
  /** Senkrechte Distanz zum Track, in Metern. */
  offTrackMeters: number;
}

// Cache fuer cumulative-distances pro Trail-track. Spart 99 % der
// Mehrfach-Berechnung bei wiederholten GPS-Ticks am selben Trail.
const cumCache = new WeakMap<CachedTrail['track'], number[]>();
function getCum(trail: CachedTrail): number[] {
  const cached = cumCache.get(trail.track);
  if (cached) return cached;
  const fresh = cumulativeDistances(trail.track.points);
  cumCache.set(trail.track, fresh);
  return fresh;
}

/**
 * @param trails - Aktuell gecachte Trails (CachedTrail[])
 * @param maxOffTrackM - Schwelle fuer "ich bin auf diesem Trail". Default 30 m.
 *                      Hoeher = mehr false-positives (Trail X wird angezeigt
 *                      obwohl du auf Parallel-Trail Y bist), niedriger = haeufiger
 *                      "kein Trail erkannt" am Trail-Rand. 30 m ist typischer
 *                      Bike-Computer-Trade-off.
 */
export function useNearestTrail(
  trails: CachedTrail[],
  maxOffTrackM: number = MAX_OFF_TRACK_M,
): NearestTrailResult | null {
  const [result, setResult] = useState<NearestTrailResult | null>(null);
  const trailsRef = useRef(trails);
  trailsRef.current = trails;

  useEffect(() => {
    const update = (coords: GeolocationCoordinates | null) => {
      if (!coords) {
        setResult(null);
        return;
      }
      const list = trailsRef.current;
      if (!list || list.length === 0) {
        setResult(null);
        return;
      }
      let best: NearestTrailResult | null = null;
      for (const t of list) {
        if (!t.track || !t.track.points || t.track.points.length < 2) continue;
        // Schnell-Check via grobe Bbox (lat/lon Spanne) — wenn die User-
        // Position weit ausserhalb der Trail-Bbox + maxOffTrack-Puffer liegt,
        // skippen wir die teure nearestPointOnTrack-Schleife. Die Bbox
        // berechnen wir on-demand und cachen sie via WeakMap (siehe oben).
        const pts = t.track.points;
        // Quick lat/lon range check.
        let minLat = pts[0]!.lat;
        let maxLat = pts[0]!.lat;
        let minLon = pts[0]!.lon;
        let maxLon = pts[0]!.lon;
        for (let i = 1; i < pts.length; i++) {
          const p = pts[i]!;
          if (p.lat < minLat) minLat = p.lat;
          if (p.lat > maxLat) maxLat = p.lat;
          if (p.lon < minLon) minLon = p.lon;
          if (p.lon > maxLon) maxLon = p.lon;
        }
        // Padding: maxOffTrackM in Grad. ~111 km/° lat, ~78 km/° lon @ 44° lat.
        const latPad = maxOffTrackM / 111000;
        const lonPad =
          maxOffTrackM /
          (111000 * Math.cos((coords.latitude * Math.PI) / 180));
        if (
          coords.latitude < minLat - latPad ||
          coords.latitude > maxLat + latPad ||
          coords.longitude < minLon - lonPad ||
          coords.longitude > maxLon + lonPad
        ) {
          continue;
        }
        const np = nearestPointOnTrack(
          pts,
          coords.latitude,
          coords.longitude,
          getCum(t),
        );
        if (np.offTrackMeters > maxOffTrackM) continue;
        if (!best || np.offTrackMeters < best.offTrackMeters) {
          best = {
            trail: t,
            alongTrackMeters: np.alongTrackMeters,
            offTrackMeters: np.offTrackMeters,
          };
        }
      }
      setResult(best);
    };
    update(positionBus.get());
    return positionBus.subscribe(update);
  }, [maxOffTrackM]);

  return result;
}
